import { DateTime } from 'luxon';

const FIXED_DATE = '2023-01-01';

/**
 * Parse date to Fixed-Date-DateTime (FDDT), which is a DateTime object in
 * which the date is fixed to 2023-01-01. This therefore acts like a Time object.
 *
 * @param {string} timeStr
 * @returns {DateTime}
 */
export function parseTimeToFddt(timeStr) {
  return DateTime.fromISO(`${FIXED_DATE}T${timeStr}`);
}

/**
 * Format an '08:00' type string from a DateTime.
 *
 * @param {DateTime} timeDt
 * @returns {string}
 */
export function formatTimeDt(timeDt) {
  return timeDt.toLocaleString(DateTime.TIME_24_SIMPLE);
}

/**
 * Get the next day from a DateTime or date string
 * @param {DateTime | string} dateOrString - DateTime object or ISO date string
 * @param {string} [timezone] - Optional timezone to use
 * @returns {DateTime}
 */
export function getNextDay(dateOrString, timezone) {
  const dt = typeof dateOrString === 'string' ? DateTime.fromISO(dateOrString) : dateOrString;
  const withZone = timezone ? dt.setZone(timezone) : dt;
  return withZone.plus({ days: 1 });
}

/**
 * Convert a date string to start of day in the given timezone, then to UTC
 * @param {string} dateStr - ISO 8601 date string
 * @param {string} timezone - Timezone identifier
 * @returns {string} ISO 8601 datetime string in UTC
 */
export function dateToStartOfDayUTC(dateStr, timezone) {
  return DateTime.fromISO(dateStr)
    .setZone(timezone)
    .startOf('day')
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

/**
 * Convert a date string to end of day in the given timezone, then to UTC
 * @param {string} dateStr - ISO 8601 date string
 * @param {string} timezone - Timezone identifier
 * @returns {string} ISO 8601 datetime string in UTC
 */
export function dateToEndOfDayUTC(dateStr, timezone) {
  return DateTime.fromISO(dateStr)
    .setZone(timezone)
    .endOf('day')
    .toUTC()
    .set({ millisecond: 0 })
    .toISO({ suppressMilliseconds: true });
}

/**
 * Convert datetime string to date in the given timezone
 * @param {string} datetimeStr - ISO 8601 datetime string (in any timezone)
 * @param {string} timezone - Timezone identifier to convert to
 * @returns {string} ISO 8601 date string
 */
export function datetimeToDateInTimezone(datetimeStr, timezone) {
  return DateTime.fromISO(datetimeStr).setZone(timezone).toISODate();
}

/**
 * Get an array of date strings from start to end date (inclusive)
 * @param {string} startDateStr - ISO 8601 date string
 * @param {string} endDateStr - ISO 8601 date string
 * @param {string} [timezone] - Optional timezone to use
 * @returns {string[]} Array of ISO 8601 date strings
 */
export function getDaysBetween(startDateStr, endDateStr, timezone) {
  const days = [];
  let current = DateTime.fromISO(startDateStr);
  const end = DateTime.fromISO(endDateStr);

  if (timezone) {
    current = current.setZone(timezone);
  }

  while (current <= end) {
    days.push(current.toISODate());
    current = current.plus({ days: 1 });
  }

  return days;
}
